// TwoPanePaper.tsx

import * as React from 'react'
import {
  Paper,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Fade,
  PaperProps,
} from '@mui/material'
import { formatCamelCase } from 'components/DataGrid/operators/formatCamelCase'
import ClearIcon from '@mui/icons-material/Clear'

type Tree = Record<string, string[]>
type Value = { base: string | null; sub: string | null; label: string | null }

// ✨ NEW: single source of truth
const MAX_MENU_HEIGHT = 380

// ✨ Change: panes inherit the parent max height and scroll
const Pane = ({ sx: sxProp, ...props }: any) => (
  <Box sx={{ minWidth: 0, overflowY: 'auto', maxHeight: 'inherit', ...sxProp }} {...props} />
)

export type TwoPanePaperProps = PaperProps & {
  tree: Tree
  filteredParents: string[]
  value: Value | null
  activeParent: string | null
  childrenItems: string[]
  onSelectParent: (parent: string) => void
  onHoverParent: (parent: string) => void
  onSelectChild: (parent: string, sub: string) => void
  onClear: () => void
}

const TwoPanePaper = React.forwardRef<HTMLDivElement, TwoPanePaperProps>(function TwoPanePaper(
  {
    tree,
    filteredParents,
    value,
    activeParent,
    childrenItems,
    onSelectParent,
    onHoverParent,
    onSelectChild,
    onClear,
    sx,
    ...paperProps
  },
  ref
) {
  return (
    <Paper
      {...paperProps}
      ref={ref}
      elevation={8}
      onMouseDown={e => e.preventDefault()} // keep popper open while clicking inside
      sx={{
        p: 1,
        width: '100%',
        boxSizing: 'border-box',
        // ✨ NEW: cap overall menu and let panes scroll
        height: MAX_MENU_HEIGHT,
        maxHeight: MAX_MENU_HEIGHT,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: 1,
          width: '100%',
          height: '100%', // ✨ so children can inherit maxHeight
        }}
      >
        {/* Left pane */}
        <Pane sx={{ pr: 1, borderRight: '1px solid', borderColor: 'divider' }}>
          {!!value?.base && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                transition: 'border-color 0.2s ease',
                gap: 1,
                px: 1,
                py: 0.5,
                mb: 0.5,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.light',
                // color: 'warning.contrastText',
                '&:hover': {
                  cursor: 'pointer',
                  borderColor: 'text.primary',
                },
              }}
              onClick={() => {
                onSelectParent?.('')
                onClear()
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.95, color: 'text.warning' }}>
                Clear to restore list
              </Typography>
              <ClearIcon fontSize="small" />
            </Box>
          )}
          <List dense disablePadding>
            {filteredParents.map(p => {
              const hasChildren = (tree[p] || []).length > 0
              const selected = value?.base === p && !value?.sub
              return (
                <ListItemButton
                  key={p}
                  selected={selected}
                  onClick={() => onSelectParent(p)}
                  onMouseEnter={() => onHoverParent(p)}
                  sx={{ py: 0.75, px: 1.25, justifyContent: 'space-between' }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={600}>
                        {formatCamelCase(p)}
                      </Typography>
                    }
                    sx={{ mr: 1 }}
                  />
                  {hasChildren && (
                    <Typography variant="body2" sx={{ opacity: 0.6, flexShrink: 0 }}>
                      ›
                    </Typography>
                  )}
                </ListItemButton>
              )
            })}
          </List>
        </Pane>

        {/* Right pane */}
        <Pane sx={{ pl: 1, overflowX: 'hidden' }}>
          {!!activeParent && (tree[activeParent] || []).length > 0 && (
            <Box key={activeParent}>
              {/* <Typography variant="caption" sx={{ px: 1.2, py: 0.5, display: 'block', opacity: 0.75 }}>
                {formatCamelCase(activeParent)}
              </Typography> */}
              {!!activeParent && (tree[activeParent] || []).length > 0 ? (
                <Box key={activeParent}>
                  <Typography variant="caption" sx={{ px: 1.2, py: 0.5, display: 'block', opacity: 0.75 }}>
                    {formatCamelCase(activeParent)}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ p: 1.2, opacity: 0.6 }}>
                  <Typography variant="caption">Hover a category to see options</Typography>
                </Box>
              )}
              <List dense disablePadding>
                {childrenItems.map((sub, idx) => {
                  const selected = value?.base === activeParent && value?.sub === sub
                  return (
                    <Fade in timeout={200} key={`${activeParent}-${sub}`} style={{ transitionDelay: `${idx * 30}ms` }}>
                      <ListItemButton
                        selected={selected}
                        sx={{ pl: 1.25 }}
                        onClick={() => onSelectChild(activeParent!, sub)}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ pl: 1.25 }}>
                              {sub}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </Fade>
                  )
                })}
              </List>
            </Box>
          )}
        </Pane>
      </Box>
    </Paper>
  )
})

export default React.memo(TwoPanePaper)
